import { useEffect, useMemo, useState } from "react";
import { useProjects, useProjectWorkItems } from "../hooks/usePm";
import { useMyTasks } from "../hooks/useMyTasks";
import { useLteTasks } from "../hooks/useLte";
import {
  lteTaskLabel,
  normalizeSearch,
  type ActivityLinks,
} from "../lib/activityLinks";
import { Icons } from "./Icons";

interface Props {
  value: ActivityLinks;
  onChange: (next: ActivityLinks) => void;
}

/** 3 champs de liaison partagés : Projet (PM, + WBS du projet choisi) / Terrain
 * (LTE) / Tâche (TM). Chacun = barre de recherche + checklist filtrée au focus,
 * chips retirables. Projet = choix UNIQUE (cocher un projet remplace le
 * précédent) : TAWA impute une activité à un seul projet (ADR « Imputation TAWA
 * → PM », 24/09/2026). Terrains et tâches restent multiples. Source unique du
 * menu unifié, utilisé au démarrage d'activité ET à la correction/créneau
 * timeline. Zéro nouveau fetch : réutilise les hooks existants. */
export function ActivityLinkFields({ value, onChange }: Props) {
  const projects = useProjects();
  const myTasks = useMyTasks();
  const lte = useLteTasks();

  const projectOptions = projects.data?.items ?? [];
  const tmOptions = myTasks.items.filter((t) => t.origin !== "temps");
  const lteOptions = lte.data?.items ?? [];

  function toggleProject(
    id: string,
    name: string | undefined,
    checked: boolean,
  ) {
    onChange({
      ...value,
      project: checked
        ? { projectId: id, projectName: name, workItemId: "" }
        : null,
    });
  }

  function updateProjectWorkItem(
    workItemId: string,
    workItemTitle: string | undefined,
  ) {
    if (!value.project) return;
    onChange({
      ...value,
      project: { ...value.project, workItemId, workItemTitle },
    });
  }

  function toggleRef(
    field: "lteTasks" | "tmTasks",
    id: string,
    label: string | undefined,
    checked: boolean,
  ) {
    onChange({
      ...value,
      [field]: checked
        ? [...value[field], { id, label }]
        : value[field].filter((t) => t.id !== id),
    });
  }

  return (
    <div className="link-fields">
      <LinkSearchField
        title="Projet"
        placeholder="Rechercher un projet…"
        color="pm"
        options={projectOptions.map((p) => ({ id: p.id, label: p.name }))}
        selectedIds={value.project ? [value.project.projectId] : []}
        onToggle={(o, checked) => toggleProject(o.id, o.label, checked)}
        emptyLabel="Aucun projet"
        renderChipExtra={(id) =>
          value.project?.projectId === id ? (
            <ProjectWorkItemPicker
              projectId={id}
              workItemId={value.project.workItemId ?? ""}
              onChange={updateProjectWorkItem}
            />
          ) : null
        }
      />

      <LinkSearchField
        title="Terrain"
        placeholder="Rechercher un terrain…"
        color="lte"
        options={lteOptions.map((t) => ({ id: t.id, label: lteTaskLabel(t) }))}
        selectedIds={value.lteTasks.map((t) => t.id)}
        onToggle={(o, checked) => toggleRef("lteTasks", o.id, o.label, checked)}
        emptyLabel="Aucun terrain"
      />

      <LinkSearchField
        title="Tâche"
        placeholder="Rechercher une tâche…"
        color="tm"
        options={tmOptions.map((t) => ({ id: t.id, label: t.title }))}
        selectedIds={value.tmTasks.map((t) => t.id)}
        onToggle={(o, checked) => toggleRef("tmTasks", o.id, o.label, checked)}
        emptyLabel="Aucune tâche"
      />
    </div>
  );
}

interface Opt {
  id: string;
  label: string;
}

/** Champ de liaison générique : recherche live (accents ignorés) → checklist
 * filtrée montrée au focus, sélection en chips retirables sous le champ. Le
 * dropdown est en flux (pas d'absolute) → aucun clipping dans la modale ; le
 * `preventDefault` sur mousedown garde le focus input pendant le multi-check. */
function LinkSearchField({
  title,
  placeholder,
  color,
  options,
  selectedIds,
  onToggle,
  emptyLabel,
  renderChipExtra,
}: {
  title: string;
  placeholder: string;
  color: "pm" | "lte" | "tm";
  options: Opt[];
  selectedIds: string[];
  onToggle: (opt: Opt, checked: boolean) => void;
  emptyLabel: string;
  renderChipExtra?: (id: string) => React.ReactNode;
}) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  const filtered = useMemo(() => {
    const q = normalizeSearch(query.trim());
    if (!q) return options;
    return options.filter((o) => normalizeSearch(o.label).includes(q));
  }, [options, query]);

  const selectedSet = new Set(selectedIds);
  const selected = options.filter((o) => selectedSet.has(o.id));

  return (
    <div className="link-search-field">
      <span className="k">
        {title}
        {selected.length > 0 && ` · ${selected.length}`}
      </span>
      <input
        className="am-search"
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {focused && (
        <div
          className="link-search-dropdown"
          onMouseDown={(e) => e.preventDefault()}
        >
          {filtered.map((o) => {
            const checked = selectedSet.has(o.id);
            return (
              <button
                key={o.id}
                type="button"
                role="option"
                aria-selected={checked}
                className={`link-search-option${checked ? " is-selected" : ""}`}
                onClick={() => onToggle(o, !checked)}
              >
                <span className="link-search-check">
                  {checked && <Icons.check size={13} sw={2.5} />}
                </span>
                {o.label}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="link-search-empty">
              {options.length === 0 ? emptyLabel : "Aucun résultat"}
            </p>
          )}
        </div>
      )}
      {selected.length > 0 && (
        <div className="link-chips">
          {selected.map((o) => (
            <span key={o.id} className={`chip chip-${color} link-chip`}>
              {o.label}
              {renderChipExtra?.(o.id)}
              <button
                type="button"
                className="link-chip-x"
                aria-label={`Retirer ${o.label}`}
                onClick={() => onToggle(o, false)}
              >
                <Icons.close size={13} sw={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Sous-picker WBS monté sur le projet choisi : le hook `useProjectWorkItems`
 * vit dans ce composant distinct, pas de violation des rules of hooks. */
function ProjectWorkItemPicker({
  projectId,
  workItemId,
  onChange,
}: {
  projectId: string;
  workItemId: string;
  onChange: (workItemId: string, workItemTitle: string | undefined) => void;
}) {
  const workItems = useProjectWorkItems(projectId);
  const items = workItems.data?.items ?? [];

  // Si le WBS choisi n'appartient plus au projet (liste rafraîchie), le vider.
  useEffect(() => {
    if (workItemId && !items.some((w) => w.id === workItemId)) {
      onChange("", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <select
      className="link-subfield"
      value={workItemId}
      onChange={(e) => {
        const w = items.find((x) => x.id === e.target.value);
        onChange(e.target.value, w?.title);
      }}
    >
      <option value="">Projet entier</option>
      {items.map((w) => (
        <option key={w.id} value={w.id}>
          {w.title}
        </option>
      ))}
    </select>
  );
}
